import { Radio, Tooltip } from "antd"
import { ModelNumberPlaceholderIcon } from "components/ui/icons/modelNumber/ModelNumberPlaceholderIcon"
import {
	defaultTooltipStyles,
	getShadowTooltipContainer,
} from "components/ui/customWebviewer/shared/shadowTooltip"
import type { TMatchResult } from "helpers/modelNumberMatch.helper"
import type { TCustomModelNumber } from "types/customModelNumbers.type"
import style from "./CustomModelNumberRow.module.scss"

const {
	row,
	rowSelected,
	radio,
	thumbnailPlaceholder,
	body,
	titleLine,
	modelNumber,
	tags,
	tag,
	tagBest,
	tagPartial,
	tagNone,
	dims,
	dimCell,
	dimLabel,
	dimValue,
	dimValueOut,
} = style

const TAG_TOOLTIPS = {
	best: "All dimensions (W, H, D) fall within the entered range",
	partial: "One or more dimensions are outside the target range",
	none: "This model does not meet the entered cabinet dimensions",
}

type Props = {
	model: TCustomModelNumber
	isSelected: boolean
	match: TMatchResult
	onSelect: (id: number) => void
}

/**
 * One row of `SelectCustomModelNumberModal`'s list — the custom-object sibling of
 * `ModelNumberRow`, deliberately smaller: no ML-choice / no-price / category-path chrome, since
 * none of those apply to a custom model number (it always carries its own price, and has no
 * category to place it in). Only the dimension-match tag applies (#1371 / #1346).
 */
export const CustomModelNumberRow = ({ model, isSelected, match, onSelect }: Props) => {
	const dimsCells = [
		{ label: "W:", value: String(model.width), inRange: match.inRange.width },
		{ label: "H:", value: String(model.height), inRange: match.inRange.height },
		{ label: "D:", value: String(model.depth), inRange: match.inRange.depth },
	]

	return (
		<div className={`${row} ${isSelected ? rowSelected : ""}`} onClick={() => onSelect(model.id)}>
			<Radio className={radio} checked={isSelected} value={model.id} />
			<span className={thumbnailPlaceholder}>
				<ModelNumberPlaceholderIcon />
			</span>
			<div className={body}>
				<div className={titleLine}>
					<span className={modelNumber}>{model.model_number}</span>
					<div className={tags}>
						{match.tag === "best" ? (
							<Tooltip
								title={TAG_TOOLTIPS.best}
								placement="top"
								getPopupContainer={getShadowTooltipContainer}
								styles={defaultTooltipStyles}
							>
								<span className={`${tag} ${tagBest}`}>Best match</span>
							</Tooltip>
						) : null}
						{match.tag === "partial" ? (
							<Tooltip
								title={TAG_TOOLTIPS.partial}
								placement="top"
								getPopupContainer={getShadowTooltipContainer}
								styles={defaultTooltipStyles}
							>
								<span className={`${tag} ${tagPartial}`}>Partial match</span>
							</Tooltip>
						) : null}
						{match.tag === "none" ? (
							<Tooltip
								title={TAG_TOOLTIPS.none}
								placement="top"
								getPopupContainer={getShadowTooltipContainer}
								styles={defaultTooltipStyles}
							>
								<span className={`${tag} ${tagNone}`}>No match</span>
							</Tooltip>
						) : null}
					</div>
				</div>
				<div className={dims}>
					{dimsCells.map((cell) => (
						<span key={cell.label} className={dimCell}>
							<span className={dimLabel}>{cell.label}</span>
							<span className={cell.inRange ? dimValue : dimValueOut}>{cell.value}</span>
						</span>
					))}
				</div>
			</div>
		</div>
	)
}
